import {faCloudArrowUp} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Blob} from 'activestorage';
import Button from 'components/Button/Button';
import ImageUploader from 'components/ImageUploader/ImageUploader';
import Text from 'components/Text/Text';
import {useGlobalState} from 'hooks/useGlobalState';
import {useSetGlobalState} from 'hooks/useSetGlobalState';
import {setErrorAC} from 'store/app/appSlice';
import {setImageBlobAC, setPreviewSrcAC} from 'store/imageUploader/imageUploaderSlice';

import './PhotoUploader.scss';

interface IProps {
  title: string;
  onCancel: () => void;
  onSetPhoto: Function;
  onRemovePhoto?: () => void;
  setIsPhotoChanging: React.Dispatch<React.SetStateAction<boolean>>;
}

const PhotoUploader = (props: IProps) => {
  const {setIsPhotoChanging, title, onCancel, onSetPhoto, onRemovePhoto = () => {}} = props;

  const dispatch = useSetGlobalState();

  const {imageBlob, previewSrc} = useGlobalState((state) => state.imageUploader);

  const onAddPhoto = (blob: Blob, files: File[], allowedFileTypes: string[]) => {
    const file = files[0];
    const fileUrl = URL.createObjectURL(file);
    const fileType = file?.name?.split('.')?.pop()?.toLowerCase();

    if (!allowedFileTypes.includes(fileType!)) {
      const fileName = file.name;
      const errorText = `The specified file
        ${fileName}
         could not be uploaded. The image is invalid or not supported.
       Allowed types: png, jpeg, gif, jpe, jpg`;

      return dispatch(setErrorAC(errorText));
    }
    dispatch(setPreviewSrcAC(fileUrl));
    return dispatch(setImageBlobAC(blob));
  };

  const handleSetPhoto = () => {
    onSetPhoto(imageBlob?.signed_id);
    onCancel();
    setIsPhotoChanging(false);
  };

  const handleCancel = () => {
    dispatch(setPreviewSrcAC(undefined));
    dispatch(setImageBlobAC(null));
    onCancel();
    setIsPhotoChanging(false);
  };

  return (
    <div className="photo-uploader">
      <div className="photo-uploader__content">
        <div className="photo-uploader__upload">
          <Text tag="h2" uppercase>
            {title}
          </Text>
          <div className="photo-uploader__box">
            <ImageUploader callback={onAddPhoto}>
              <div
                className="photo-uploader__uploader"
                style={{backgroundImage: `url(${previewSrc})`, backgroundSize: 'cover'}}
              >
                <div className="photo-uploader__wrapper">
                  {!previewSrc && (
                    <div className="photo-uploader__icon">
                      <FontAwesomeIcon icon={faCloudArrowUp} />
                      <Text tag="h4">Drop your image here</Text>
                      <Text tag="span">or</Text>
                    </div>
                  )}
                  <Button variant="primary">Browse Photo</Button>
                </div>
              </div>
            </ImageUploader>
            <div className="photo-uploader__mobile-wrapper">
              <div className="photo-uploader__mobile-delete-photo">
                <Button variant="link" onClick={onRemovePhoto}>
                  Delete Current Photo
                </Button>
              </div>
              <ImageUploader callback={onAddPhoto}>
                <div className="photo-uploader__uploader-mobile">
                  <Button variant="primary">Upload From Gallery</Button>
                </div>
              </ImageUploader>
            </div>
          </div>
          <div className="photo-uploader__info">
            <div>
              <Text className="photo-uploader__info-text" tag="p">
                Recommended dimensions 300x300px. Maximum upload file size 20MB.
              </Text>
            </div>
            <div className="photo-uploader__file-types">
              <Text tag="p" className="photo-uploader__file-types-text">
                Accepted file types:
                <Text tag="span" className="photo-uploader__types">
                  .gif .jpe .jpeg .jpg .png
                </Text>
              </Text>
            </div>
          </div>
        </div>
      </div>
      <div className="photo-uploader__buttons">
        <Button variant="text-white" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="primary" disabled={!imageBlob} onClick={handleSetPhoto}>
          Set Photo
        </Button>
      </div>
    </div>
  );
};

export default PhotoUploader;
